/** @format */

import template from './gb-app.html';

export const GbAppComponent = {
    template,
    controller: class GbAppComponent {
        constructor(
            $gbProjector,
            $log,
            $stateParams,
            $ccRealtime,
            LoadingHelper,
            $ccLoading,
            $currency,
            $gbAuction,
            $gbOneTimeUseToken,
            $gbAuth,
            $location,
            $galabidUrl,
            $ccPusher,
            $document,
            $q,
            $gbSyncAuctionData
        ) {
            'ngInject';

            this.$log = $log;
            this.$gbProjector = $gbProjector;
            this.$stateParams = $stateParams;
            this.$ccRealtime = $ccRealtime;
            this.$currency = $currency;
            this.$gbAuction = $gbAuction;
            this.$gbOneTimeUseToken = $gbOneTimeUseToken;
            this.$gbAuth = $gbAuth;
            this.$location = $location;
            this.$galabidUrl = $galabidUrl;
            this.$ccPusher = $ccPusher;
            this.$document = $document;
            this.$q = $q;
            this.$gbSyncAuctionData = $gbSyncAuctionData;

            this.loadingHelper = new LoadingHelper();
            this.isLoading = () => this.loadingHelper.isLoading();
            this.getLoadingMessage = () => this.loadingHelper.getFirstMessage();
            this.loadingWatcher = $ccLoading.bind(this.isLoading, this.getLoadingMessage);

            this.syncingInitial = false;
            this.syncError = null;
            this.$notifPreviewEvent = '$gbNotifPreview';
            this.showFullscreenToggleMs = 2000;
            this.$raffleDrawPreviewEvent = '$gbRaffleDrawPreview';
            this.$raffleWinnerPreviewEvent = '$gbRaffleWinnerPreview';
            this.$honourRollPreviewEvent = '$gbHonourRollPreview';
        }

        isEditMode() {
            return this.$stateParams.editMode === 'true';
        }

        getAuctionUrl() {
            return this.$stateParams.auctionUrl;
        }

        getProjectorId() {
            return this.$stateParams.projectorId;
        }

        getProjector() {
            return this.$gbProjector.cache.single(this.getProjectorId());
        }

        hasError() {
            return !!this.syncError;
        }

        syncProjector() {
            return this.$gbProjector
                .get({
                    auction: this.getAuctionUrl(),
                    uuid: this.getProjectorId(),
                })
                .$promise.then((data) => {
                    this.$log.info('Synched Projector', data);
                    this.$gbProjector.cache.push(data);
                })
                .catch((err) => {
                    this.syncError = err;
                    throw err;
                });
        }

        syncAuction() {
            return this.$gbAuction
                .get({
                    url_name: this.getAuctionUrl(),
                })
                .$promise.then((data) => {
                    this.$log.info('Synched Auction', data);
                    this.$gbAuction.cache.push(data);
                    this.auction = data;
                })
                .catch((err) => {
                    this.syncError = err;
                    throw err;
                });
        }

        getAuction() {
            return this.auction;
        }

        getCurrencySymbol() {
            return this.$currency.get(this.getAuction().currency).symbol;
        }

        authPusher() {
            let authEndpoint = `${this.$galabidUrl.getApiUrl()}pusher/auth/`;
            this.$ccPusher.getClient().config.authEndpoint = authEndpoint;

            this.$ccPusher.getClient().config.auth = {
                headers: {
                    Authorization: `Token ${this.$gbAuth.getTokenCookie()}`,
                },
            };
        }

        initPusher() {
            this.auctionRealtimeChannel = `auction@${this.getAuctionUrl()}`;
            this.$ccRealtime.register(this.auctionRealtimeChannel);

            this.projectorRealtimeChannel = `projector@${this.getProjectorId()}`;
            this.$ccRealtime.register(this.projectorRealtimeChannel);

            if (this.$gbAuth.isAuthed()) {
                this.authPusher();

                this.privateRealtimeChannel = `private-auction@${this.getAuctionUrl()}`;
                this.$ccRealtime.register(this.privateRealtimeChannel);
            }
        }

        getTempToken() {
            return this.$stateParams.token;
        }

        resolvePresenterToken() {
            let loader = this.loadingHelper.push('LOADING');

            return this.$gbOneTimeUseToken
                .resolveToken(this.getTempToken())
                .then((data) => {
                    this.$log.info('Resolved Presenter Token', data);
                    this.$gbAuth.setTokenCookie(data.actual_token);
                    return data;
                })
                .catch((err) => {
                    this.syncError = err;
                    throw err;
                })
                .finally(() => {
                    loader();
                });
        }

        clearTokenQueryParam() {
            this.$location.search('token', null);
        }

        syncData() {
            let promises = [
                this.syncAuction(),
                this.syncProjector(),
                this.$gbSyncAuctionData.syncAllResources(this.getAuctionUrl()),
            ];

            return this.$q.all(promises);
        }

        initialSync() {
            let loader = this.loadingHelper.push('LOADING');
            this.syncingInitial = true;

            return this.syncData().finally(() => {
                loader();
                this.syncingInitial = false;
            });
        }

        setWindowFocusWatcher() {
            this.refocusWatcherDestroyer = this.onWindowRefocus(() => {
                if (!this.synching) {
                    this.$log.info('Resyncing Projector App Data');
                    this.syncData();
                }
            });
        }

        onWindowRefocus(callback) {
            let handler = () => {
                if (this.$document[0].visibilityState === 'visible') {
                    this.$log.info('Window Projector Refocus Watcher Called');
                    callback();
                }
            };

            this.$document.on('visibilitychange', handler);

            let destroyer = () => {
                this.$log.info('Cancelled Projector Window Refocus Watcher', handler);
                this.$document.off('visibilitychange', handler);
            };

            return destroyer;
        }

        $onInit() {
            this.syncingInitial = true;

            this.initPusher();

            if (this.isEditMode() && !!this.getTempToken()) {
                this.resolvePresenterToken().then(() => {
                    this.clearTokenQueryParam();
                    this.initialSync();
                });
            } else {
                this.initialSync();
            }

            this.setWindowFocusWatcher();
        }

        $onDestroy() {
            this.loadingWatcher();

            this.$ccRealtime.unregister(this.auctionRealtimeChannel);
            this.$ccRealtime.unregister(this.projectorRealtimeChannel);
            this.$ccRealtime.unregister(this.privateRealtimeChannel);

            this.refocusWatcherDestroyer();
        }
    },
};
